/**
 * CollectionRemindersSettings (R4 — Settings → Financial)
 * Toggle + multi-email chips input for collection reminder emails.
 * Settings stored as tenant_settings keys:
 *   collection_reminders_enabled (bool)
 *   collection_reminders_recipients (comma-separated emails)
 */
import { useState, useEffect, useRef } from 'react';
import {
  useCollectionReminderSettings,
  useUpdateCollectionReminderSettings,
} from '@/hooks/useCollectionFollowups';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Mail, X, Bell } from 'lucide-react';
import { isValidEmail } from '@/lib/email-validation';

function parseEmails(csv: string): string[] {
  return csv
    .split(',')
    .map((e) => e.trim())
    .filter(Boolean);
}

interface EmailChipsProps {
  emails: string[];
  onAdd: (email: string) => void;
  onRemove: (email: string) => void;
  disabled?: boolean;
}

function EmailChips({ emails, onAdd, onRemove, disabled }: EmailChipsProps) {
  const [inputValue, setInputValue] = useState('');
  const [error, setError] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const commitInput = () => {
    const trimmed = inputValue.trim();
    if (!trimmed) return;
    if (!isValidEmail(trimmed)) {
      setError('Invalid email address');
      return;
    }
    if (emails.includes(trimmed)) {
      setError('Already added');
      return;
    }
    onAdd(trimmed);
    setInputValue('');
    setError('');
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',' || e.key === 'Tab') {
      e.preventDefault();
      commitInput();
    } else if (e.key === 'Backspace' && !inputValue && emails.length > 0) {
      onRemove(emails[emails.length - 1]);
    }
  };

  return (
    <div
      className="flex flex-wrap gap-1.5 p-2 border border-gray-300 rounded-md min-h-[42px] cursor-text bg-white"
      onClick={() => inputRef.current?.focus()}
    >
      {emails.map((email) => (
        <span
          key={email}
          className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-100 text-blue-800 rounded text-sm"
        >
          {email}
          {!disabled && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onRemove(email);
              }}
              className="hover:text-blue-600 ml-0.5"
              aria-label={`Remove ${email}`}
            >
              <X size={12} />
            </button>
          )}
        </span>
      ))}
      {!disabled && (
        <input
          ref={inputRef}
          type="email"
          value={inputValue}
          onChange={(e) => {
            setInputValue(e.target.value);
            setError('');
          }}
          onKeyDown={handleKeyDown}
          onBlur={commitInput}
          placeholder={emails.length === 0 ? 'Add email address…' : ''}
          className="flex-1 min-w-[160px] border-none outline-none text-sm bg-transparent"
          data-testid="email-chips-input"
        />
      )}
      {error && (
        <span className="w-full text-xs text-destructive">{error}</span>
      )}
    </div>
  );
}

export function CollectionRemindersSettings() {
  const { data, isLoading } = useCollectionReminderSettings();
  const updateSettings = useUpdateCollectionReminderSettings();

  const [enabled, setEnabled] = useState(false);
  const [emails, setEmails] = useState<string[]>([]);
  const [hasChanges, setHasChanges] = useState(false);

  // Initialize from fetched data
  useEffect(() => {
    if (data) {
      setEnabled(data.collection_reminders_enabled);
      setEmails(parseEmails(data.collection_reminders_recipients));
      setHasChanges(false);
    }
  }, [data]);

  const handleToggle = (checked: boolean) => {
    setEnabled(checked);
    setHasChanges(true);
    // Auto-disable if no recipients when toggling on isn't useful,
    // but allow the toggle to reflect intent — save handles validation
  };

  const handleAddEmail = (email: string) => {
    setEmails((prev) => [...prev, email]);
    setHasChanges(true);
  };

  const handleRemoveEmail = (email: string) => {
    const next = emails.filter((e) => e !== email);
    setEmails(next);
    setHasChanges(true);
    // Auto-disable if last recipient removed
    if (next.length === 0 && enabled) {
      setEnabled(false);
    }
  };

  const handleSave = () => {
    // If enabled but no recipients, disable automatically
    const finalEnabled = enabled && emails.length > 0;
    updateSettings.mutate(
      {
        collection_reminders_enabled: finalEnabled,
        collection_reminders_recipients: emails.join(','),
      },
      {
        onSuccess: () => setHasChanges(false),
      },
    );
  };

  if (isLoading) {
    return (
      <div className="space-y-3 py-2">
        <Skeleton className="h-5 w-48" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-9 w-24" />
      </div>
    );
  }

  return (
    <div className="space-y-4 py-2" data-testid="collection-reminders-settings">
      {/* Description */}
      <p className="text-sm text-gray-600">
        Send a daily email at 7:30 AM listing all collection follow-ups due today.
        Separate from the operational daily digest — independent subscribe path.
      </p>

      {/* Enable toggle */}
      <div className="flex items-center gap-3">
        <button
          type="button"
          role="switch"
          aria-checked={enabled}
          onClick={() => handleToggle(!enabled)}
          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 ${
            enabled ? 'bg-primary' : 'bg-gray-200'
          }`}
          data-testid="reminders-enabled-toggle"
        >
          <span
            className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-sm transition-transform ${
              enabled ? 'translate-x-6' : 'translate-x-1'
            }`}
          />
        </button>
        <span className="text-sm font-medium text-gray-700">
          {enabled ? 'Collection reminder emails enabled' : 'Collection reminder emails disabled'}
        </span>
      </div>

      {/* Recipients */}
      <div className="space-y-1.5">
        <label className="block text-sm font-medium text-gray-700">
          <Mail size={14} className="inline mr-1.5" />
          Recipients
        </label>
        <EmailChips
          emails={emails}
          onAdd={handleAddEmail}
          onRemove={handleRemoveEmail}
        />
        <p className="text-xs text-gray-500">
          Press Enter or comma to add an address. Removing the last recipient disables the toggle.
        </p>
      </div>

      {/* Save */}
      <Button
        onClick={handleSave}
        disabled={!hasChanges || updateSettings.isPending}
        size="sm"
        data-testid="save-reminders-settings"
      >
        {updateSettings.isPending ? 'Saving…' : 'Save'}
      </Button>

      {!enabled && emails.length > 0 && (
        <p className="text-xs text-amber-600 flex items-center gap-1">
          <Bell size={12} />
          Toggle enabled above to activate reminders for these recipients.
        </p>
      )}
      {enabled && emails.length === 0 && (
        <p className="text-xs text-destructive">
          Add at least one recipient to receive reminder emails.
        </p>
      )}
    </div>
  );
}
